import SegmentedControl from "@lhd/components/SegmentedControl";
import ModalConfirm from "@lhd/components/Modal/ModalConfirm";
import { UNSAVED_CHANGES_WARNING_MESSAGE } from "@lhd/constants";
import { useEffect, useState } from "react";

type Props = Omit<
  React.ComponentProps<typeof SegmentedControl>,
  "value" | "onChange"
> & {
  defaultValue?: any;
  onChange?: (value: any) => boolean | void;
  disableWhenValue?: string;
  showPopupList?: string[];
  showPopup?: boolean;
  confirmContent?: string;
};
export const useControlStatement = ({
  options,
  defaultValue,
  disableWhenValue,
  showPopupList = [],
  confirmContent = UNSAVED_CHANGES_WARNING_MESSAGE,
  onChange,
  ...restProps
}: Props) => {
  const [value, setValue] = useState(defaultValue || options[0].value);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const showPopup = showPopupList.includes(value) || restProps.showPopup;

  useEffect(() => {
    if (!showPopup) setPendingValue(null);
  }, [showPopup]);

  const commitChange = (nextValue: string) => {
    setPendingValue(null);
    if (onChange?.(nextValue) === false) return;
    setValue(nextValue);
  };

  const handleChange = (nextValue: string) => {
    // SegmentedControl also calls onChange when its value prop changes.
    if (nextValue === value) return;

    if (showPopup) {
      setPendingValue(nextValue);
      return;
    }

    commitChange(nextValue);
  };

  const handleConfirm = () => {
    if (pendingValue === null) return;
    commitChange(pendingValue);
  };

  const elementSegmentedControl = (
    <>
      <SegmentedControl
        options={options}
        value={value}
        {...restProps}
        disabled={restProps.disabled || value === disableWhenValue}
        onChange={handleChange}
      />
      {showPopup && (
        <ModalConfirm
          isOpen={pendingValue !== null}
          content={confirmContent}
          onCancel={() => setPendingValue(null)}
          onOk={handleConfirm}
          text_scale={restProps.displaySize}
        />
      )}
    </>
  );
  return {
    value,
    setValue,
    elementSegmentedControl,
  };
};
